import { InlineAddTable, InlineRow } from '../../../../components/inline-add-table/InlineAddTable';
import { TdInput } from '../../../../components/inline-add-table/cells';
import { blankTraining, isBlankTraining, isUntitledTraining, type DraftTraining } from '../utils/trainingRecommendationRows';

/**
 * The Training Recommendations inline-add grid (product call, 13 Oct 2026) — a row per
 * recommendation: its title, and what is recommended. ONE component for the plan's Create form
 * and for the plan profile's own Create, so the two cannot ask for a recommendation two ways.
 *
 * The grid always holds a blank row, which is left out on save; there is no new row while one is
 * still blank. The cell ids are `${idPrefix}-training-recommendations.{row}.{field}` — what a
 * refusal naming a row is focused by.
 */
export function TrainingRecommendationsGrid({
  idPrefix,
  rows,
  onChange,
  disabled,
}: {
  idPrefix: string;
  rows: DraftTraining[];
  onChange: (next: DraftTraining[]) => void;
  disabled: boolean;
}) {
  const update = (key: string, patch: Partial<DraftTraining>) =>
    onChange(rows.map((t) => (t.key === key ? { ...t, ...patch } : t)));

  return (
    <>
      <InlineAddTable
        headers={['Training title *', 'Training recommendation']}
        colWidths={[320, 520]}
        flexColumn={1}
        resizeKey={`${idPrefix}-training-recommendations-v1`}
        rowCount={rows.length}
        onAddRow={() => onChange([...rows, blankTraining()])}
        addDisabled={disabled || rows.some(isBlankTraining)}
        addDisabledReason={disabled ? 'This is being saved.' : undefined}
        addButtonId={`${idPrefix}-add-training-recommendation`}
      >
        {rows.map((row, index) => (
          <InlineRow key={row.key} idx={index + 1} onRemove={() => onChange(rows.filter((t) => t.key !== row.key))}>
            <TdInput
              id={`${idPrefix}-training-recommendations.${index}.title`}
              ariaLabel={`Training title on row ${index + 1}`}
              placeholder="The training's name"
              value={row.title}
              onChange={(next) => update(row.key, { title: next.slice(0, 300) })}
              disabled={disabled}
            />
            <TdInput
              id={`${idPrefix}-training-recommendations.${index}.details`}
              ariaLabel={`Training recommendation on row ${index + 1}`}
              placeholder="What is recommended, and why"
              value={row.details}
              onChange={(next) => update(row.key, { details: next.slice(0, 2000) })}
              disabled={disabled}
            />
          </InlineRow>
        ))}
      </InlineAddTable>

      {rows.some(isUntitledTraining) ? (
        <p role="alert" className="text-xs text-red-600">
          Enter a title for each training recommendation.
        </p>
      ) : null}
    </>
  );
}
